import { gql } from '@apollo/client';
import {
	useCreateFishboneBranchRiskLinkMutation,
	useCreateFishboneItemRiskLinkMutation,
	useUpdateFishboneRiskLinkStrengthMutation,
	useDeleteFishboneRiskLinkMutation,
	useCreateFishboneBranchStakeLinkMutation,
	useCreateFishboneItemStakeLinkMutation,
	useUpdateFishboneStakeLinkStrengthMutation,
	useDeleteFishboneStakeLinkMutation,
	useCreateFishboneBranchIndicatorLinkMutation,
	useCreateFishboneItemIndicatorLinkMutation,
	useUpdateFishboneIndicatorLinkStrengthMutation,
	useDeleteFishboneIndicatorLinkMutation,
	useCreateFishboneBranchStakeholderLinkMutation,
	useCreateFishboneItemStakeholderLinkMutation,
	useUpdateFishboneStakeholderLinkStrengthMutation,
	useDeleteFishboneStakeholderLinkMutation
} from 'graph/dist/generated';

// Une cible = un type de lien. `risk` couvre risks ET ouvertures (meme table
// Risk filtree sur isOpportunity). `field` est le champ forward expose sur
// FishboneBranch/FishboneItem ; `targetType` le __typename de l'objet cible
// (pour la maj cache inverse, affichage bidirectionnel).
export type FishboneLinkTarget = 'risk' | 'stake' | 'indicator' | 'stakeholder';

export const TARGET_CONFIG: Record<
	FishboneLinkTarget,
	{ typeName: string; field: string; targetIdName: string; targetType: string }
> = {
	risk: {
		typeName: 'FishboneRiskLink',
		field: 'riskLinks',
		targetIdName: 'riskId',
		targetType: 'Risk'
	},
	stake: {
		typeName: 'FishboneStakeLink',
		field: 'stakeLinks',
		targetIdName: 'stakeId',
		targetType: 'Stake'
	},
	indicator: {
		typeName: 'FishboneIndicatorLink',
		field: 'indicatorLinks',
		targetIdName: 'indicatorId',
		targetType: 'Indicator'
	},
	stakeholder: {
		typeName: 'FishboneStakeholderLink',
		field: 'stakeholderLinks',
		targetIdName: 'stakeholderId',
		targetType: 'Stakeholder'
	}
};

const nodeTypename = (nodeType: string) =>
	nodeType === 'branch' ? 'FishboneBranch' : 'FishboneItem';

export const useFishboneLinkController = () => {
	// Un couple create branch/item + delete + updateStrength par cible.
	const hooks = {
		risk: {
			createBranch: useCreateFishboneBranchRiskLinkMutation()[0],
			createItem: useCreateFishboneItemRiskLinkMutation()[0],
			update: useUpdateFishboneRiskLinkStrengthMutation()[0],
			delete: useDeleteFishboneRiskLinkMutation()[0]
		},
		stake: {
			createBranch: useCreateFishboneBranchStakeLinkMutation()[0],
			createItem: useCreateFishboneItemStakeLinkMutation()[0],
			update: useUpdateFishboneStakeLinkStrengthMutation()[0],
			delete: useDeleteFishboneStakeLinkMutation()[0]
		},
		indicator: {
			createBranch: useCreateFishboneBranchIndicatorLinkMutation()[0],
			createItem: useCreateFishboneItemIndicatorLinkMutation()[0],
			update: useUpdateFishboneIndicatorLinkStrengthMutation()[0],
			delete: useDeleteFishboneIndicatorLinkMutation()[0]
		},
		stakeholder: {
			createBranch: useCreateFishboneBranchStakeholderLinkMutation()[0],
			createItem: useCreateFishboneItemStakeholderLinkMutation()[0],
			update: useUpdateFishboneStakeholderLinkStrengthMutation()[0],
			delete: useDeleteFishboneStakeholderLinkMutation()[0]
		}
	};

	// --- Create ---
	const createLink = async (
		target: FishboneLinkTarget,
		nodeType: 'branch' | 'item',
		nodeId: string,
		targetId: string
	) => {
		const cfg = TARGET_CONFIG[target];
		const methodName =
			nodeType === 'branch'
				? `createFishboneBranch${capitalize(target)}Link`
				: `createFishboneItem${capitalize(target)}Link`;
		const hook =
			nodeType === 'branch'
				? hooks[target].createBranch
				: hooks[target].createItem;
		const nodeIdArg =
			nodeType === 'branch' ? 'fishboneBranchId' : 'fishboneItemId';

		const result = await hook({
			variables: { [nodeIdArg]: nodeId, [cfg.targetIdName]: targetId },
			update(cache, { data }) {
				const created = data?.[methodName];
				if (!created?.id) return;
				const newLink = cache.writeFragment({
					id: cfg.typeName + ':' + created.id,
					data: created,
					fragment: gql`
						fragment ${cfg.typeName}New on ${cfg.typeName} {
							id
							fishboneNodeId
							nodeType
							${cfg.targetIdName}
							strength
							createdAt
						}
					`
				});
				// Forward : champ *Links sur la branche/sous-arete
				cache.modify({
					id: `${nodeTypename(nodeType)}:${nodeId}`,
					fields: {
						[cfg.field](existing = []) {
							return [...existing, newLink];
						}
					}
				});
				// Inverse : fishboneLinks sur l'objet cible (bidirectionnel)
				cache.modify({
					id: `${cfg.targetType}:${targetId}`,
					fields: {
						fishboneLinks(existing = []) {
							return [...existing, newLink];
						}
					}
				});
			},
			optimisticResponse: {
				[methodName]: {
					__typename: cfg.typeName,
					id: 'temp-' + nodeId + '-' + targetId,
					fishboneNodeId: nodeId,
					nodeType,
					[cfg.targetIdName]: targetId,
					strength: 1,
					createdAt: new Date().toISOString()
				}
			}
		});

		return result?.data?.[methodName];
	};

	// --- Delete ---
	const deleteLink = async (
		target: FishboneLinkTarget,
		linkId: string,
		nodeType: 'branch' | 'item',
		nodeId: string,
		targetId: string
	) => {
		const cfg = TARGET_CONFIG[target];
		const methodName = `deleteFishbone${capitalize(target)}Link`;
		await hooks[target].delete({
			variables: { id: linkId },
			update(cache) {
				const dropById = (existing: any[] = [], { readField }: any) =>
					existing.filter(
						(ref: any) => readField('id', ref) !== linkId
					);
				cache.modify({
					id: `${nodeTypename(nodeType)}:${nodeId}`,
					fields: { [cfg.field]: dropById }
				});
				cache.modify({
					id: `${cfg.targetType}:${targetId}`,
					fields: { fishboneLinks: dropById }
				});
			},
			optimisticResponse: { [methodName]: true }
		});
	};

	// --- Update strength (cycle 1->2->3) ---
	const updateLinkStrength = async (
		target: FishboneLinkTarget,
		link: any,
		strength: number
	) => {
		const cfg = TARGET_CONFIG[target];
		const methodName = `updateFishbone${capitalize(target)}LinkStrength`;
		await hooks[target].update({
			variables: { id: link.id, strength },
			update(cache) {
				cache.modify({
					id: `${cfg.typeName}:${link.id}`,
					fields: { strength: () => strength }
				});
			},
			optimisticResponse: { [methodName]: true }
		});
	};

	return { createLink, deleteLink, updateLinkStrength };
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
